import { useCallback, useEffect, useRef, useState } from 'react'
import { PaymentResult } from '../types/PaymentResult'
import { createLogger } from '../utils/logger'
import config from '../Constants'

type MercureState = {
    connected: boolean
    lastMessage: PaymentResult | null
    error: Event | null
}

const logger = createLogger('usePaymentResultMercure')

export const usePaymentResultMercure = (
    paymentPageId: string,
    transactionId: number,
    enabled: boolean = true,
) => {
    const topic = `urn:payment:status:${paymentPageId}:${transactionId}`

    const [state, setState] = useState<MercureState>({
        connected: false,
        lastMessage: null,
        error: null,
    })

    const esRef = useRef<EventSource | null>(null)

    const disconnect = useCallback(() => {
        if (esRef.current) {
            esRef.current.close()
            esRef.current = null
        }
    }, [])

    useEffect(() => {
        if (!enabled) {
            logger.debug('Mercure disabled, skipping EventSource creation')

            return
        }

        logger.debug(`Creating EventSource for topic: ${topic}`)

        const url = new URL(config.url.MERCURE_URL)
        url.searchParams.append('topic', topic)

        // Если есть Last-Event-ID, EventSource автоматически его отправит
        const es = new EventSource(url.toString())
        esRef.current = es

        es.onopen = () => {
            logger.info('EventSource opened successfully')

            setState((prev) => ({ ...prev, connected: true, error: null }))
        }

        es.onmessage = (event) => {
            logger.debug(`EventSource message received (Last id: ${event.lastEventId || 'no'})`, event.data)

            try {
                setState((prev) => (
                    { ...prev, lastMessage: JSON.parse(event.data) as PaymentResult }
                ))
            } catch (error) {
                logger.warn('Failed to parse EventSource message', error)
            }
        }

        es.onerror = (e) => {
            logger.error('EventSource error occurred', e)

            setState((prev) => ({ ...prev, connected: false, error: e }))
        }

        return () => {
            logger.debug('Cleanup: closing EventSource')

            es.close()
        }
    }, [topic, enabled])

    return {
        paymentResult: state.lastMessage,
        connected: state.connected,
        error: state.error,
        disconnect,
    }
}
